import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { TwitterShareButton, FacebookShareButton, TwitterIcon, FacebookIcon } from 'react-share';
import './ProductDetail.css';
import MarketplaceHeader from '../MarketPlaceHeader/marketPlaceHeader';
// ⚠️ Adapte ce chemin selon l'emplacement de ton dossier data
import {
  allProducts, categories, COLOR_HEX, PLACEHOLDER,
  getProductById, imageFor, colorImageFor, readIds, writeIds,
} from '../data/product';

const MAX_QTY = 10;
const fmt = (n) => `${n.toLocaleString('fr-FR')} €`;

const imgFallback = (e) => {
  if (!e.currentTarget.src.endsWith(PLACEHOLDER)) e.currentTarget.src = PLACEHOLDER;
};

const Stars = ({ rating = 0 }) => (
  <span className="pd-stars" role="img" aria-label={`Note : ${rating} sur 5`}>
    {'★'.repeat(rating)}
    <span className="pd-stars-dim">{'★'.repeat(5 - rating)}</span>
  </span>
);

/* Avis d'exemple : remplace par les vrais avis de ton API */
const buildReviews = (name) => [
  { name: 'Sarah M.', rating: 5, date: '12/09/2026', comment: `Très satisfaite : ${name} est conforme aux photos et bien emballé.` },
  { name: 'Karim B.', rating: 4, date: '30/08/2026', comment: 'Bonne qualité pour le prix. Je recommande.' },
  { name: 'Lina T.', rating: 4, date: '14/08/2026', comment: 'Produit correct, livraison dans les temps.' },
];

const TABS = [
  { key: 'description', label: 'Description' },
  { key: 'details', label: 'Caractéristiques' },
  { key: 'reviews', label: 'Avis' },
];

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tabsRef = useRef(null);
  const product = useMemo(() => getProductById(id), [id]);

  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [activeThumb, setActiveThumb] = useState('base');
  const [brokenThumbs, setBrokenThumbs] = useState([]);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [cartIds, setCartIds] = useState(() => readIds('mwg_cart'));
  const [favIds, setFavIds] = useState(() => readIds('mwg_favorites'));
  const [search, setSearch] = useState('');

  const goCategory = (category) => navigate('/Category', { state: { category } });

  /* Réinitialise l'état quand on change de produit */
  useEffect(() => {
    setQuantity(1);
    setActiveTab('description');
    setSelectedColor(product?.colors?.[0] || '');
    setSelectedSize('');
    setActiveThumb('base');
    setBrokenThumbs([]);
    setIsZoomOpen(false);
    window.scrollTo({ top: 0 });
  }, [product]);

  /* Recherche depuis le header → page catégorie (après une courte pause de frappe) */
  useEffect(() => {
    const q = search.trim();
    if (!q) return undefined;
    const t = setTimeout(() => navigate('/Category', { state: { search: q } }), 600);
    return () => clearTimeout(t);
  }, [search, navigate]);

  /* Échap ferme le zoom */
  useEffect(() => {
    if (!isZoomOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setIsZoomOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isZoomOpen]);

  const similar = useMemo(() => {
    if (!product) return [];
    return allProducts
      .filter((p) => p.id !== product.id && p.category === product.category)
      .sort((a, b) => Number(b.subcategory === product.subcategory) - Number(a.subcategory === product.subcategory))
      .slice(0, 4);
  }, [product]);

  const headerEl = (
    <MarketplaceHeader
      categories={categories}
      activeCategory={product ? product.category : ''}
      onCategoryChange={goCategory}
      searchValue={search}
      onSearchChange={setSearch}
      cartCount={cartIds.length}
      favCount={favIds.length}
    />
  );

  /* ── Produit introuvable ── */
  if (!product) {
    return (
      <div className="pd-page">
        {headerEl}
        <main className="pd-main">
          <div className="pd-panel pd-empty">
            <h1>Produit introuvable</h1>
            <p>Ce produit n'existe pas ou n'est plus disponible.</p>
            <button type="button" className="pd-btn pd-btn-primary" onClick={() => goCategory('')}>
              Retour à la boutique
            </button>
          </div>
        </main>
      </div>
    );
  }

  /* ── Calculs d'affichage ── */
  const onSale = product.oldPrice && product.oldPrice > product.price;
  const discount = onSale ? Math.round(100 - (product.price / product.oldPrice) * 100) : 0;
  const isFav = favIds.includes(product.id);
  const reviews = buildReviews(product.name);

  const thumbs = ['base', ...(product.colors || [])];
  const visibleThumbs = thumbs.filter((t) => !brokenThumbs.includes(t));
  const thumbSrc = (t) => (t === 'base' ? imageFor(product) : colorImageFor(product, t));
  const mainSrc = activeThumb !== 'base' && !brokenThumbs.includes(activeThumb)
    ? colorImageFor(product, activeThumb)
    : imageFor(product);

  const markBroken = (t) => setBrokenThumbs((b) => (b.includes(t) ? b : [...b, t]));

  const onMainError = (e) => {
    if (activeThumb !== 'base' && !brokenThumbs.includes(activeThumb)) {
      markBroken(activeThumb);
      return;
    }
    imgFallback(e);
  };

  const specs = [
    ['Catégorie', product.category],
    ['Sous-catégorie', product.subcategory],
    ['Marque', product.brand],
    ['Dimensions', product.dimensions],
    ['Matières', product.materials],
    ['Poids', product.weight],
    ['Couleurs', product.colors && product.colors.join(', ')],
    ['Tailles', product.sizes && product.sizes.join(', ')],
    ['Disponibilité', product.inStock ? 'En stock' : 'Rupture de stock'],
  ].filter(([, value]) => value);

  /* ── Actions ── */
  const selectColor = (color) => {
    setSelectedColor(color);
    setActiveThumb(color);
  };

  const changeQty = (delta) => setQuantity((q) => Math.min(MAX_QTY, Math.max(1, q + delta)));

  const addToCart = () => {
    if (!product.inStock) return toast.error('Ce produit est en rupture de stock.');
    if (product.sizes?.length && !selectedSize) return toast.error('Choisissez une taille avant d\'ajouter au panier.');
    const next = [...cartIds, ...Array(quantity).fill(product.id)];
    setCartIds(next);
    writeIds('mwg_cart', next);
    return toast.success(`${product.name} (×${quantity}) ajouté au panier`);
  };

  const toggleFavorite = () => {
    const next = isFav ? favIds.filter((x) => x !== product.id) : [...favIds, product.id];
    setFavIds(next);
    writeIds('mwg_favorites', next);
    toast.info(isFav ? 'Retiré des favoris' : 'Ajouté aux favoris');
  };

  const goToReviews = () => {
    setActiveTab('reviews');
    tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const shareUrl = window.location.href;
  const shareText = `Découvrez ${product.name} sur MWGTD`;

  return (
    <div className="pd-page">
      {headerEl}

      <main className="pd-main">
        {/* Fil d'Ariane */}
        <nav className="pd-crumb" aria-label="Fil d'Ariane">
          <button type="button" onClick={() => goCategory('')}>Accueil</button>
          <span aria-hidden="true">/</span>
          <button type="button" onClick={() => goCategory(product.category)}>{product.category}</button>
          <span aria-hidden="true">/</span>
          <b>{product.name}</b>
        </nav>

        {/* ── Haut de fiche : galerie + infos ── */}
        <section className="pd-top">
          <div className="pd-gallery">
            <div className="pd-frame">
              <img
                className="pd-img"
                src={mainSrc}
                alt={`${product.name}${selectedColor ? ` – ${selectedColor}` : ''}`}
                onClick={() => setIsZoomOpen(true)}
                onError={onMainError}
              />
              {onSale && <span className="pd-badge">-{discount}%</span>}
              {!product.inStock && <span className="pd-badge pd-badge-out">Rupture</span>}
              <button
                type="button"
                className={`pd-fav${isFav ? ' on' : ''}`}
                onClick={toggleFavorite}
                aria-pressed={isFav}
                aria-label={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              >
                {isFav ? '♥' : '♡'}
              </button>
              <button type="button" className="pd-zoom-hint" onClick={() => setIsZoomOpen(true)}>
                ⤢ Agrandir
              </button>
            </div>

            {visibleThumbs.length > 1 && (
              <div className="pd-thumbs">
                {visibleThumbs.map((t) => (
                  <button
                    type="button"
                    key={t}
                    className={`pd-thumb${activeThumb === t ? ' on' : ''}`}
                    onClick={() => (t === 'base' ? setActiveThumb('base') : selectColor(t))}
                    aria-label={t === 'base' ? 'Vue principale' : `Voir en ${t}`}
                  >
                    <img
                      src={thumbSrc(t)}
                      alt=""
                      loading="lazy"
                      onError={(e) => (t === 'base' ? imgFallback(e) : markBroken(t))}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pd-info pd-panel">
            <div className="pd-brand">{product.brand}</div>
            <h1 className="pd-title">{product.name}</h1>

            <div className="pd-rating">
              <Stars rating={product.rating} />
              <button type="button" className="pd-link" onClick={goToReviews}>
                {product.rating}/5 · {product.reviews} avis
              </button>
            </div>

            <div className="pd-price-row">
              <span className="pd-price">{fmt(product.price)}</span>
              {onSale && (
                <>
                  <span className="pd-old">{fmt(product.oldPrice)}</span>
                  <span className="pd-save">Économisez {fmt(product.oldPrice - product.price)}</span>
                </>
              )}
            </div>

            <p className={`pd-stock ${product.inStock ? 'ok' : 'no'}`}>
              ● {product.inStock ? 'En stock' : 'Rupture de stock'}
            </p>

            <p className="pd-lead">{product.description}</p>

            {product.colors?.length > 0 && (
              <div className="pd-opt">
                <div className="pd-opt-label">Couleur : <b>{selectedColor}</b></div>
                <div className="pd-swatches">
                  {product.colors.map((c) => (
                    <button
                      type="button"
                      key={c}
                      className={`pd-sw${selectedColor === c ? ' on' : ''}`}
                      style={{ background: COLOR_HEX[c] || '#666' }}
                      title={c}
                      aria-label={`Couleur ${c}`}
                      aria-pressed={selectedColor === c}
                      onClick={() => selectColor(c)}
                    />
                  ))}
                </div>
              </div>
            )}

            {product.sizes?.length > 0 && (
              <div className="pd-opt">
                <div className="pd-opt-label">Taille : <b>{selectedSize || 'à choisir'}</b></div>
                <div className="pd-sizes">
                  {product.sizes.map((s) => (
                    <button
                      type="button"
                      key={s}
                      className={`pd-size${selectedSize === s ? ' on' : ''}`}
                      aria-pressed={selectedSize === s}
                      onClick={() => setSelectedSize(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="pd-buy-row">
              <div className="pd-qty" role="group" aria-label="Quantité">
                <button type="button" onClick={() => changeQty(-1)} disabled={quantity <= 1} aria-label="Diminuer la quantité">−</button>
                <output aria-live="polite">{quantity}</output>
                <button type="button" onClick={() => changeQty(1)} disabled={quantity >= MAX_QTY} aria-label="Augmenter la quantité">+</button>
              </div>
              <div className="pd-total">Total <b>{fmt(product.price * quantity)}</b></div>
            </div>

            <div className="pd-actions">
              <button type="button" className="pd-btn pd-btn-primary" onClick={addToCart} disabled={!product.inStock}>
                {product.inStock ? 'Ajouter au panier' : 'Indisponible'}
              </button>
              <button type="button" className={`pd-btn pd-btn-ghost${isFav ? ' on' : ''}`} onClick={toggleFavorite}>
                {isFav ? '♥ Dans mes favoris' : '♡ Ajouter aux favoris'}
              </button>
            </div>

            <div className="pd-share">
              <span>Partager</span>
              <TwitterShareButton url={shareUrl} title={shareText}>
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <FacebookShareButton url={shareUrl} quote={shareText}>
                <FacebookIcon size={32} round />
              </FacebookShareButton>
            </div>
          </div>
        </section>

        {/* ── Onglets : description / caractéristiques / avis ── */}
        <section className="pd-tabs pd-panel" ref={tabsRef}>
          <div className="pd-tablist" role="tablist" aria-label="Informations produit">
            {TABS.map((t) => (
              <button
                type="button"
                key={t.key}
                role="tab"
                id={`tab-${t.key}`}
                aria-selected={activeTab === t.key}
                aria-controls={`panel-${t.key}`}
                className={`pd-tab${activeTab === t.key ? ' on' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}{t.key === 'reviews' ? ` (${product.reviews})` : ''}
              </button>
            ))}
          </div>

          <div className="pd-tabpanel" role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
            {activeTab === 'description' && (
              <>
                <p className="pd-text">{product.description}</p>
                <ul className="pd-points">
                  <li>Marque : {product.brand}</li>
                  {product.materials && <li>Matières : {product.materials}</li>}
                  {product.dimensions && <li>{product.dimensions}</li>}
                  {product.sizes && <li>Tailles disponibles : {product.sizes.join(', ')}</li>}
                </ul>
              </>
            )}

            {activeTab === 'details' && (
              <dl className="pd-specs">
                {specs.map(([label, value]) => (
                  <div className="pd-spec" key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {activeTab === 'reviews' && (
              <>
                <div className="pd-review-sum">
                  <span className="pd-review-score">{product.rating}<small>/5</small></span>
                  <div>
                    <Stars rating={product.rating} />
                    <div className="pd-muted">Basé sur {product.reviews} avis</div>
                  </div>
                </div>
                <ul className="pd-reviews">
                  {reviews.map((r) => (
                    <li key={r.name} className="pd-review">
                      <div className="pd-review-head">
                        <b>{r.name}</b>
                        <Stars rating={r.rating} />
                        <span className="pd-muted">{r.date}</span>
                      </div>
                      <p>{r.comment}</p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </section>

        {/* ── Produits similaires ── */}
        {similar.length > 0 && (
          <section className="pd-similar" aria-labelledby="pd-similar-title">
            <h2 id="pd-similar-title">Vous aimerez aussi</h2>
            <div className="pd-sim-grid">
              {similar.map((p) => (
                <button type="button" key={p.id} className="pd-sim-card" onClick={() => navigate(`/product/${p.id}`)}>
                  <img src={imageFor(p)} alt={p.name} loading="lazy" onError={imgFallback} />
                  <span className="pd-sim-body">
                    <span className="pd-sim-brand">{p.brand}</span>
                    <span className="pd-sim-name">{p.name}</span>
                    <span className="pd-sim-price">{fmt(p.price)}</span>
                    <span className={`pd-sim-stock ${p.inStock ? 'ok' : 'no'}`}>
                      ● {p.inStock ? 'Disponible' : 'Rupture'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Barre d'achat fixe (mobile) */}
      <div className="pd-buybar">
        <div className="pd-buybar-price">
          <b>{fmt(product.price * quantity)}</b>
          {quantity > 1 && <small>{quantity} × {fmt(product.price)}</small>}
        </div>
        <button type="button" className="pd-btn pd-btn-primary" onClick={addToCart} disabled={!product.inStock}>
          {product.inStock ? 'Ajouter au panier' : 'Indisponible'}
        </button>
      </div>

      {/* Zoom image */}
      {isZoomOpen && (
        <div className="pd-zoom" role="dialog" aria-modal="true" aria-label="Image agrandie" onClick={() => setIsZoomOpen(false)}>
          <button type="button" className="pd-zoom-close" aria-label="Fermer" onClick={() => setIsZoomOpen(false)}>✕</button>
          <img src={mainSrc} alt={product.name} onClick={(e) => e.stopPropagation()} onError={onMainError} />
        </div>
      )}

      <ToastContainer position="top-right" autoClose={2500} theme="dark" />
    </div>
  );
};

export default ProductDetail;